/**
 * E7 PR 0b — the ONE way the share view leaves its session: to a full page (the link's front door on re-entry or
 * after a Log out / revoke, the identity picker, an identity switch's form POST) or to ANOTHER share session inside
 * the SPA. The session ends FIRST (`endSession`, the session-ended helper), synchronously, then the view moves — so a
 * request of the old session still out (a Bin restore) is aborted and its answer dropped.
 *
 * A move to a share session of the SAME identity (a sub-session of this one, an entry landing) does not end anything:
 * `goToShareSession(…, { ends: false })` says so at the call site. `sessionEnded.test.ts` fails on a navigation to
 * the share front door or another share session made anywhere else.
 */
import type { NavigateFunction } from 'react-router-dom'
import { endSession, sessionContextMoveAccountedFor, sessionContextOf, type SessionEndReason } from '@papan/core-ui/ui/sessionEnded'
import { openFileNavPath } from './openFileNav'
import { claimPageLeave } from '../../lib/reloadAfterAccountEnd'

/** The visit is (or may be) over — a Log out, a revoke announced, a sign-out in another tab: end the session NOW,
 *  synchronously, before the async check that decides whether to leave (`leaveShareView`). */
export function endShareVisit(reason: SessionEndReason): void {
  endSession(reason)
}

/** Leave the share view for a full page; the session ends first. #1316 review round 2: a no-op navigation when a drive
 *  account end's load (to the link's front door, `reloadAfterAccountEnd`) already started — whichever starts first wins. */
export function leaveShareView(href: string, reason: SessionEndReason, opts: { replace?: boolean } = {}): void {
  endSession(reason)
  if (!claimPageLeave('share')) return
  if (opts.replace) window.location.replace(href)
  else window.location.assign(href)
}

/** Leave by submitting a form that navigates the page (an identity switch's POST); the session ends first. */
export function submitShareLeave(form: HTMLFormElement, reason: SessionEndReason): void {
  endSession(reason)
  if (!claimPageLeave('share')) return
  form.submit()
}

/** Where an openFile / app launch answer goes (`openFileNavPath`): a share sub-session of THIS identity keeps the
 *  session; the visitor's own drive (`spaPath`) LEAVES the visit — it ends first ('share-left'). False = nowhere. */
export function goToOpenFileTarget(navigate: NavigateFunction, body: { sessionId?: string; spaPath?: string } | null): boolean {
  const path = openFileNavPath(body)
  if (!path) return false
  if (path.startsWith('/share-view/')) goToShareSession(navigate, path, { ends: false })
  else {
    endSession('share-left')
    sessionContextMoveAccountedFor('drive:')   // the drive of whichever tenant the visitor lands on
    navigate(path)
  }
  return true
}

/** Move to another share session inside the SPA. `ends`: the reason when it is ANOTHER identity's / browser's session
 *  (this one ends first); `false` for a session of the same identity (a sub-session, a landing). */
export function goToShareSession(navigate: NavigateFunction, path: string, opts: { ends: SessionEndReason | false; replace?: boolean }): void {
  if (opts.ends) endSession(opts.ends)
  // either way this move is accounted for: the route's context backstop (useTenant) must not end it (again) — set only
  // for a move that changes the context (a landing on the CURRENT session would leave a mark a later Back used)
  const target = sessionContextOf(path, null)
  const here = typeof window === 'undefined' ? '' : sessionContextOf(window.location.pathname, null)
  if (target && target !== here) sessionContextMoveAccountedFor(target)
  if (opts.replace !== undefined) navigate(path, { replace: opts.replace })
  else navigate(path)
}
